import { useApolloClient } from '@apollo/client';
import { isEmpty } from '@newfront-insurance/core';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { useCallback, useMemo } from 'react';

import { OrphanedStreamRecovery } from './orphaned-stream-recovery';
import type { ChatAppType, GetChatsByFiltersChat } from '../../../queries/chats';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { updateChatsListAfterStream } from '../lib/notification-helpers';
import { shouldRecoverOrphanedStream } from '../lib/orphaned-streams';
import { NovaChatProvider } from '../providers';

interface OrphanedStreamsContainerProps {
  chats: GetChatsByFiltersChat[];
}

/**
 * Container component that manages recovery of orphaned streams.
 * Detects chats with activeStreamId (excluding the currently active chat)
 * and creates renderless recovery components for each one.
 *
 * Each orphaned stream gets its own NovaChatProvider instance to ensure
 * proper isolation and stream resumability.
 */
export function OrphanedStreamsContainer({ chats }: OrphanedStreamsContainerProps) {
  const { chatUuid, userUuid, authProvider, apiEndpoint, userScopes, notificationController, effectiveAppType } =
    useChatOverlayContext();

  // Find orphaned streams: chats with activeStreamId that are not the current chat
  const orphanedStreams = useMemo(() => {
    return chats.filter((chat) =>
      shouldRecoverOrphanedStream({
        apiEndpoint,
        chat,
        currentChatUuid: chatUuid,
      }),
    );
  }, [apiEndpoint, chats, chatUuid]);

  // Don't render anything if no orphaned streams or missing required context
  if (orphanedStreams.length === 0 || isEmpty(userUuid) || !authProvider) {
    return null;
  }

  browserLogger.debug('[Orphaned Streams Container] Found orphaned streams', {
    count: orphanedStreams.length,
    chatUuids: orphanedStreams.map((chat) => chat.uuid),
  });

  return (
    <>
      {orphanedStreams.map((chat) => (
        <OrphanedStreamProvider
          key={chat.uuid}
          chat={chat}
          userUuid={userUuid}
          authProvider={authProvider}
          apiEndpoint={apiEndpoint}
          scopes={userScopes}
          effectiveAppType={effectiveAppType}
          onOrphanedStreamFinish={notificationController.onOrphanedStreamFinish}
        />
      ))}
    </>
  );
}

/**
 * Provider wrapper for a single orphaned stream.
 * Fetches initial messages and wraps the recovery component in NovaChatProvider.
 */
function OrphanedStreamProvider({
  chat,
  userUuid,
  authProvider,
  apiEndpoint,
  scopes,
  effectiveAppType,
  onOrphanedStreamFinish,
}: {
  chat: GetChatsByFiltersChat;
  userUuid: string;
  authProvider: ReturnType<typeof useChatOverlayContext>['authProvider'];
  apiEndpoint: ReturnType<typeof useChatOverlayContext>['apiEndpoint'];
  scopes?: string[];
  effectiveAppType?: ChatAppType | null;
  onOrphanedStreamFinish: (params: {
    chatUuid: string;
    chatTitle: string;
    isStandaloneMode?: boolean;
  }) => Promise<void>;
}) {
  const apolloClient = useApolloClient();
  const chatTitle = chat.title || 'New Chat';
  const handleReconnectUnavailable = useCallback(
    (reconnectChatUuid: string) => {
      updateChatsListAfterStream({
        apolloClient,
        chatUuid: reconnectChatUuid,
        userUuid,
        appType: effectiveAppType,
      });
    },
    [apolloClient, effectiveAppType, userUuid],
  );

  return (
    <NovaChatProvider
      userUuid={userUuid}
      accountUuid={chat.accountUuid ?? undefined}
      apiEndpoint={apiEndpoint}
      authProvider={authProvider}
      scopes={scopes}
      initialMessages={[]}
    >
      <OrphanedStreamRecovery
        activeStreamId={chat.activeStreamId}
        chatUuid={chat.uuid!}
        chatTitle={chatTitle}
        initialMessages={[]}
        onReconnectUnavailable={handleReconnectUnavailable}
        onStreamFinish={onOrphanedStreamFinish}
      />
    </NovaChatProvider>
  );
}
